/**
 * Request and response schemas for the encounter and clinical-note surface (`docs/roadmap.md` Phase
 * 3, C5/C6), matching `authRequests.ts`'s own split from its domain module: `encounters.ts` and
 * `noteAttestation.ts` hold the pure rules, this file holds the wire shapes both the server and the
 * web app validate against, so the two cannot drift.
 *
 * Every object schema is strict, same reasoning as `authRequests.ts`: an unrecognised key is a
 * rejection, not a silently ignored extra. No server-owned value appears in any request schema —
 * `patientId` and `encounterId`/`noteId` travel as URL path parameters, not body fields, and nothing
 * here accepts `authorId`, `signedBy`, or `cosignedBy`, all of which are the caller's own identity,
 * resolved server-side from the session (`claude-kit/rules/invariants.md`, "Server-authoritative
 * values never come from the request").
 */

import { z } from 'zod';

import { RESTRICTION_REASONS } from './confidentiality.ts';
import { ENCOUNTER_STATUSES } from './encounters.ts';
import { signatureSettingsSchema } from './signatureRequests.ts';

export const MAX_ENCOUNTER_TYPE_LENGTH = 100;
export const MAX_NOTE_TYPE_LENGTH = 100;
export const MIN_NOTE_CONTENT_LENGTH = 1;
/** Generous for a clinical note's free text, and still a bound — matching `authRequests.ts`'s own
 * reasoning for `MAX_PASSWORD_LENGTH`: not a defence against cost, a defence against an unbounded
 * body being copied through the request pipeline. */
export const MAX_NOTE_CONTENT_LENGTH = 20_000;

/** A record's confidentiality label, as the caller may set it. Unknown reasons are a rejection here
 * — unlike `confidentiality.ts`'s `labelOf`, which drops them because it also has to parse data an
 * older or newer build wrote, a fresh request has no such excuse. */
const confidentialityReasonsSchema = z
  .array(z.enum(RESTRICTION_REASONS))
  .max(RESTRICTION_REASONS.length);

// --- Encounters --------------------------------------------------------------------------------

/**
 * Start a visit. No `patientId` — that is the URL's `:patientId`. No `orgId` — the session's own
 * tenant scope. `providerId` defaults to the caller in the service layer when omitted, which is the
 * common case (a clinician starting their own visit); a nurse or medical assistant rooming a patient
 * ahead of the physician may name a different provider explicitly.
 */
export const createEncounterRequestSchema = z.strictObject({
  encounterType: z.string().trim().min(1).max(MAX_ENCOUNTER_TYPE_LENGTH),
  status: z.enum(ENCOUNTER_STATUSES).optional(),
  /** ISO 8601 with offset. Omitted means "now", resolved server-side. */
  startedAt: z.iso.datetime({ offset: true }).optional(),
  providerId: z.uuid().optional(),
  confidentialityReasons: confidentialityReasonsSchema.optional(),
});
export type CreateEncounterRequest = z.infer<typeof createEncounterRequestSchema>;

/**
 * Update a visit already in progress: move its status, or record when it ended. At least one field
 * is required — a body with neither has nothing to update and is a caller error, not a no-op.
 */
export const updateEncounterRequestSchema = z
  .strictObject({
    status: z.enum(ENCOUNTER_STATUSES).optional(),
    endedAt: z.iso.datetime({ offset: true }).optional(),
  })
  .refine((value) => value.status !== undefined || value.endedAt !== undefined, {
    error: 'provide status, endedAt, or both',
  });
export type UpdateEncounterRequest = z.infer<typeof updateEncounterRequestSchema>;

export const encounterResponseSchema = z.strictObject({
  id: z.uuid(),
  patientId: z.uuid(),
  providerId: z.uuid(),
  encounterType: z.string(),
  status: z.string(),
  startedAt: z.string(),
  endedAt: z.string().nullable(),
  confidentialityReasons: z.array(z.string()),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type EncounterResponse = z.infer<typeof encounterResponseSchema>;

// --- Clinical notes ------------------------------------------------------------------------------

/**
 * Create a draft note. No `authorId` — the caller, resolved server-side; no `status` — every note
 * starts life as `draft` (`noteAttestation.ts`). `encounterId` is optional, matching
 * `clinicalNotes.encounterId`'s own nullability: a phone note or a portal-message reply is real
 * documentation with no formal encounter behind it.
 */
export const createNoteRequestSchema = z.strictObject({
  encounterId: z.uuid().optional(),
  noteType: z.string().trim().min(1).max(MAX_NOTE_TYPE_LENGTH),
  content: z.string().trim().min(MIN_NOTE_CONTENT_LENGTH).max(MAX_NOTE_CONTENT_LENGTH),
  confidentialityReasons: confidentialityReasonsSchema.optional(),
});
export type CreateNoteRequest = z.infer<typeof createNoteRequestSchema>;

/** Edit a draft's content. Refused server-side once the note is signed — see `canEditNote`. */
export const updateNoteRequestSchema = z.strictObject({
  content: z.string().trim().min(MIN_NOTE_CONTENT_LENGTH).max(MAX_NOTE_CONTENT_LENGTH),
});
export type UpdateNoteRequest = z.infer<typeof updateNoteRequestSchema>;

export const noteResponseSchema = z.strictObject({
  id: z.uuid(),
  patientId: z.uuid(),
  encounterId: z.uuid().nullable(),
  authorId: z.uuid(),
  noteType: z.string(),
  content: z.string(),
  status: z.string(),
  signedAt: z.string().nullable(),
  signedBy: z.uuid().nullable(),
  /** Copied from the signer's `user_signatures` row the moment `signedAt`/`signedBy` were set —
   * never a live read of their current settings. See `db/schema/clinical.ts`'s own comment on
   * `signedSignatureSnapshot` for why. Null exactly when `signedAt` is null, or when the signer had
   * never set a mark at the moment they signed. */
  signedSignature: signatureSettingsSchema.nullable(),
  cosignedAt: z.string().nullable(),
  cosignedBy: z.uuid().nullable(),
  /** Same snapshot discipline as `signedSignature`, taken at `cosignedAt`. */
  cosignedSignature: signatureSettingsSchema.nullable(),
  confidentialityReasons: z.array(z.string()),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type NoteResponse = z.infer<typeof noteResponseSchema>;

// --- Failure vocabulary --------------------------------------------------------------------------

/**
 * The failure codes this surface can answer with, as a closed union so the web app can branch on
 * them without matching on prose — same reasoning as `authRequests.ts`'s `AUTH_ERROR_CODES`.
 */
export const CLINICAL_ERROR_CODES = [
  /** The body failed schema validation. */
  'invalid_request',
  /** The role holds the capability in general, but `careTeam.ts`'s `decideChartAccess` refused this
   * specific patient. The response also carries `denialReason`. */
  'chart_access_denied',
  /** The role does not hold the capability this route requires at all. */
  'forbidden',
  /** No such encounter or note in the caller's own tenant. */
  'not_found',
  /** `updateEncounterRequestSchema`'s status would not be a legal move from the current one. */
  'invalid_transition',
  /** The note is not a draft, or the caller did not author it — see `canEditNote`. */
  'note_not_editable',
  /** The note is not a draft, or the caller did not author it — see `canSignNote`. */
  'note_not_signable',
  /** The note has not been signed yet — see `canCosignNote`. */
  'note_not_cosignable',
  /** The caller already signed this note; a co-sign needs a second, distinct reviewer. */
  'cannot_cosign_own_signature',
  /** `BACKLOG.md` C6b: the caller holds `note:cosign` but no active, unexpired MD/DO credential on
   * file — see `@emr/shared`'s `hasActiveCosignCredential`. */
  'no_active_credential',
  /** `BACKLOG.md` C6a: someone else already co-signed this note between the caller's read and this
   * write — the database-level compare-and-swap in `notesService.ts`'s `cosignNote` caught it. */
  'already_cosigned',
  /** The audit trail could not be written, so the action did not happen (`docs/roadmap.md` §3.2). */
  'audit_unavailable',
] as const;
export type ClinicalErrorCode = (typeof CLINICAL_ERROR_CODES)[number];
